import { Badge, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { CampaignStateLabels } from '@/utils/campaign-labels';
import type { Campaign } from '@/services/db/campaign/entity';

type BadgeColor = 'grey' | 'yellow' | 'red' | 'green';

/**
 * Badge colour per campaign state. Exhaustive over `Campaign['state']` rather than falling back to
 * a default, so adding a state to the API contract is a compile error instead of a silent grey
 * chip. The transient backend states (`evaluating`, `paused`, `deleting`) take grey.
 */
export const STATE_BADGE_COLORS = {
  running: 'green',
  draft: 'grey',
  stopped: 'red',
  archived: 'grey',
  sunsetting: 'red',
  stopping: 'red',
  archiving: 'grey',
  evaluating: 'grey',
  paused: 'grey',
  deleting: 'grey',
} as const satisfies Record<Campaign['state'], BadgeColor>;

type CampaignStateBadgeProps = {
  campaign: Campaign;
};

/**
 * The campaign's state chip with its explanatory tooltip.
 *
 * A separate "Scheduled" chip for `scheduledStart`/`scheduledStop` is intentionally not part of
 * this component: those values belong with the scheduling UI rather than the canvas chrome.
 */
export function CampaignStateBadge({ campaign }: CampaignStateBadgeProps) {
  const taxonomy = useProductTaxonomy();
  const { state } = campaign;

  const label = CampaignStateLabels[state] ?? state;
  const color = STATE_BADGE_COLORS[state];
  const tooltipContent = getStateTooltipContent(state, taxonomy);

  return (
    <Tooltip placement="bottom">
      <Badge color={color} showStatusIndicator>
        {label}
      </Badge>
      {tooltipContent == null ? null : <TooltipContent>{tooltipContent}</TooltipContent>}
    </Tooltip>
  );
}

type Taxonomy = ReturnType<typeof useProductTaxonomy>;

/**
 * Per-state tooltip copy. Returns `null` for the states that have no explanatory copy, so the
 * badge renders without a tooltip rather than with an empty one.
 */
function getStateTooltipContent(state: Campaign['state'], taxonomy: Taxonomy): string | null {
  const term = taxonomy.term('campaigns.titleSingular');
  const people = taxonomy.term('people.plural');
  const customers = taxonomy.term('customers.plural');

  switch (state) {
    case 'archiving':
      return `This ${term} is in the process of being archived`;
    case 'archived':
      return `${term} is archived and doesn't send any messages`;
    case 'draft':
      return `Not yet matching ${customers} or sending messages`;
    case 'running':
      return `${term} triggers and message workflow are live`;
    case 'sunsetting':
      return `This ${term} will stop after all in progress ${people} have finished their journey`;
    case 'stopping':
      return `This ${term} is in the process of being stopped.`;
    case 'stopped':
      return `${term} is stopped and doesn't send any messages`;
    default:
      return null;
  }
}
